import React, { useState, useEffect } from "react";
import { useApi } from "../apiHelpers";
import { useToast } from "../components/Toast/ToastContext";
import useUserStore from "../stores/useUserStore";
import useFleetStore from "../stores/useFleetStore";
import LogoSpinner from "../components/ui/LogoSpinner";
import PinModal from "./PinModal";
import { instance, isSwarm } from "../instance";
import PasswordModal from "./PasswordModal";
import ConnectedBots from "./ConnectedBots";
import AvatarSettings from "./AvatarSettings";
import { useDeviceMode } from "../Scanner/useDeviceMode";
import SetSharedDeviceModal from "../Scanner/SetSharedDeviceModal";
import PageLayout from '../components/ui/PageLayout';
import Panel from '../components/ui/Panel';

import { buttonClass } from '../components/ui/Button';
import { Field, FieldRow } from '../components/ui/Field';
import Checkbox from '../components/ui/Checkbox';
import Switch from '../components/ui/Switch';
import Reveal from '../components/ui/Reveal';
import FadeIn from '../components/ui/FadeIn';
const SettingsPage = () => {
  // The Pilot Simulator easter egg used to live here (five taps on this header).
  // It moved to the dashboard greeting: fun/PilotSimulator/WelcomeSecret.tsx.
  const api = useApi();
  const toast = useToast();
  const user = useUserStore((state) => state.user);
  const setUser = useUserStore((state) => state.setUser);
  // Fleet visibility is a FIREFLEET concept: it manages its own F drones plus a
  // handful of Swarm D-series ones, so there is a choice to make and can_swarm
  // gates it. SwarmSystems manages one fleet and will never hold Firefly drones,
  // so there is nothing to choose and the card is not rendered at all. The API
  // agrees — resolveFleetPrefs ignores stored prefs there (dronefleet-router.js).
  const canSwarm = !isSwarm && (user.isadmin || user.can_admin || user.can_swarm);
  // Charge Control is open to pilots/operations/managers (admins bypass); the
  // VPN-access toggle below only shows for those users.
  const canCharge = user.isadmin || user.can_admin || user.can_pilot || user.can_operations || user.can_manager;

  // Profile data from API
  const [profile, setProfile] = useState(null);
  // Slack link status for the Connections card (null until loaded).
  const [slackLink, setSlackLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Edit states
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState(""); // Password for email change

  // Camera settings (localStorage)
  const [camCompat, setCamCompat] = useState(1);

  // Notification preferences
  const [rmaUpdates, setRmaUpdates] = useState(true);
  const [maintenanceAlerts, setMaintenanceAlerts] = useState(true);
  const [newEventEmail, setNewEventEmail] = useState(false); // opt-in, default off

  // Fleet visibility toggles — which brands appear in the drone list
  const [showFirefly, setShowFirefly] = useState(true);
  const [showSwarm, setShowSwarm] = useState(false);

  // Charge Control: "I have VPN access" gates the Open-with-VPN deep link.
  const [hasVpnAccess, setHasVpnAccess] = useState(false);

  // Modal states
  const [showPinModal, setShowPinModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showSharedDeviceModal, setShowSharedDeviceModal] = useState(false);

  // Per-device scanner mode (localStorage). Only the one-way "set shared"
  // affordance lives here — reverting requires clearing site data.
  const [deviceMode, setDeviceMode] = useDeviceMode();

  // Track unsaved changes
  const [hasProfileChanges, setHasProfileChanges] = useState(false);
  const [hasNotificationChanges, setHasNotificationChanges] = useState(false);
  const [hasFleetChanges, setHasFleetChanges] = useState(false);

  // Fetch profile on mount (Slack link status rides along)
  useEffect(() => {
    fetchProfile();
    fetchSlackLink();
    loadLocalSettings();
    if (canCharge) {
      api.get("/charge/prefs")
        .then((p) => setHasVpnAccess(!!p.has_vpn_access))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // VPN-access is a single immediate toggle (no save button), persisted to charge prefs.
  const toggleVpnAccess = async (val) => {
    setHasVpnAccess(val);
    try {
      await api.put("/charge/prefs", { body: { has_vpn_access: val } });
      toast.success(val ? "VPN access enabled" : "VPN access disabled");
    } catch (error) {
      setHasVpnAccess(!val); // revert on failure
      toast.error("Failed to save VPN access setting");
    }
  };

  const fetchSlackLink = async () => {
    try {
      const data = await api.get("/receipts/slack-link", { silent: true });
      setSlackLink(data);
    } catch (error) {
      // Non-fatal: the card just shows the DM instructions without a status.
      setSlackLink({ linked: false, slack_app_id: null });
    }
  };

  const fetchProfile = async () => {
    try {
      const data = await api.get("/users/me");
      setProfile(data);
      setFirstname(data.firstname || "");
      setLastname(data.lastname || "");
      setEmail(data.email || "");
      setRmaUpdates(data.notificationPrefs?.rma_updates ?? true);
      setMaintenanceAlerts(data.notificationPrefs?.maintenance_alerts ?? true);
      setNewEventEmail(data.notificationPrefs?.new_event_email ?? false);
      setShowFirefly(data.fleetPrefs?.firefly ?? true);
      setShowSwarm(data.fleetPrefs?.swarm ?? false);
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const loadLocalSettings = () => {
    const savedCamCompat = parseInt(localStorage.getItem("camCompat")) || 1;
    setCamCompat(savedCamCompat);
  };

  // Track profile changes
  useEffect(() => {
    if (!profile) return;
    const changed =
      firstname !== (profile.firstname || "") ||
      lastname !== (profile.lastname || "") ||
      email !== (profile.email || "");
    setHasProfileChanges(changed);
  }, [firstname, lastname, email, profile]);

  // Track notification changes
  useEffect(() => {
    if (!profile) return;
    const changed =
      rmaUpdates !== (profile.notificationPrefs?.rma_updates ?? true) ||
      maintenanceAlerts !== (profile.notificationPrefs?.maintenance_alerts ?? true) ||
      newEventEmail !== (profile.notificationPrefs?.new_event_email ?? false);
    setHasNotificationChanges(changed);
  }, [rmaUpdates, maintenanceAlerts, newEventEmail, profile]);

  // Track fleet-visibility changes
  useEffect(() => {
    if (!profile) return;
    const changed =
      showFirefly !== (profile.fleetPrefs?.firefly ?? true) ||
      showSwarm !== (profile.fleetPrefs?.swarm ?? false);
    setHasFleetChanges(changed);
  }, [showFirefly, showSwarm, profile]);

  const saveProfile = async () => {
    // Validate required fields
    if (!firstname.trim()) {
      toast.error("First name is required");
      return;
    }
    if (!lastname.trim()) {
      toast.error("Last name is required");
      return;
    }
    if (!email.trim()) {
      toast.error("Email is required");
      return;
    }

    setSaving(true);
    try {
      const updates = {};
      if (firstname !== profile.firstname) updates.firstname = firstname.trim();
      if (lastname !== profile.lastname) updates.lastname = lastname.trim();

      // Email change requires password
      if (email !== profile.email) {
        if (!emailPassword) {
          toast.error("Password required to change email");
          setSaving(false);
          return;
        }
        updates.email = email.trim();
        updates.password = emailPassword;
      }

      await api.patch("/users/me", { body: updates });

      // Update Zustand store with new name/email
      setUser({
        ...user,
        firstname: firstname,
        lastname: lastname,
        email: email,
      });

      toast.success("Profile updated");
      setEmailPassword("");
      fetchProfile(); // Refresh from server
    } catch (error) {
      console.error("Error saving profile:", error);
      const errorMsg = error?.message || "Failed to save profile";
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const saveNotifications = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me/notifications", {
        body: {
          rma_updates: rmaUpdates,
          maintenance_alerts: maintenanceAlerts,
          new_event_email: newEventEmail,
        },
      });
      toast.success("Notification preferences saved");
      fetchProfile();
    } catch (error) {
      console.error("Error saving notifications:", error);
      toast.error("Failed to save notification preferences");
    } finally {
      setSaving(false);
    }
  };

  const saveFleetPrefs = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me/fleet-prefs", {
        body: { firefly: showFirefly, swarm: showSwarm },
      });
      toast.success("Fleet visibility saved");
      await fetchProfile();
      // Re-pull the fleet so the new visibility takes effect right away
      // (the /fleet endpoint reads the saved pref server-side).
      useFleetStore.getState().refreshFleet();
    } catch (error) {
      console.error("Error saving fleet prefs:", error);
      toast.error("Failed to save fleet visibility");
    } finally {
      setSaving(false);
    }
  };

  const handleCamCompatChange = (e) => {
    const value = parseInt(e.target.value);
    setCamCompat(value);
    localStorage.setItem("camCompat", value.toString());
    toast.success("Camera mode saved");
  };

  if (loading) return <LogoSpinner label="Loading settings" />;

  return (
    <PageLayout kind="focus" className="pt-4">
<div>
      <h1 className="text-2xl font-bold text-primary mb-6">
        Settings
      </h1>

      {/* Profile Section */}
      <Panel title="Profile" className="mb-4">

        <div className="space-y-4">
          <FieldRow label="First Name">
            <Field value={firstname} onChange={(e) => setFirstname(e.target.value)} />
          </FieldRow>

          <FieldRow label="Last Name">
            <Field value={lastname} onChange={(e) => setLastname(e.target.value)} />
          </FieldRow>

          <div>
            <FieldRow label="Email">
              <Field type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </FieldRow>
            <Reveal show={email !== profile?.email}>
              <FieldRow label="Enter password to confirm email change" className="mt-2">
                <Field
                  type="password"
                  warn
                  value={emailPassword}
                  onChange={(e) => setEmailPassword(e.target.value)}
                  placeholder="Current password"
                />
              </FieldRow>
            </Reveal>
          </div>

          {/* User Type - hidden for now, needs expansion
          <div>
            <label className="block text-sm text-on-surface-variant mb-1">User Type</label>
            <div className="px-3 py-2 bg-[#3a3a3a] border border-outline rounded text-on-surface-variant">
              {profile?.usertype || "N/A"}
              {profile?.isadmin && (
                <span className="ml-2 px-2 py-0.5 bg-primary/20 text-primary-ink text-xs rounded">
                  Admin
                </span>
              )}
            </div>
          </div>
          */}

          <Reveal show={!!hasProfileChanges}>
            <button
              onClick={saveProfile}
              disabled={saving}
              className={`${buttonClass('filled', 'md')} w-full`}
            >
              {saving ? "Saving..." : "Save Profile"}
            </button>
          </Reveal>
        </div>
      </Panel>

      <AvatarSettings initialColor={profile?.avatar_color} initialShape={profile?.avatar_shape} initialAccessory={profile?.avatar_accessory} />

      {/* Security Section */}
      <Panel title="Security" className="mb-4">

        <div className="space-y-4">
          {/* PIN */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-white font-medium">PIN</div>
              <div className="text-sm text-on-surface-variant">
                {profile?.hasPin ? (
                  <span className="flex items-center gap-2">
                    <span className="text-green-400">Set</span>
                    <span className="tracking-widest">****</span>
                  </span>
                ) : (
                  <span className="text-yellow-400">Not set</span>
                )}
              </div>
              <div className="text-xs text-on-surface-variant mt-1">
                Required for Fireflasher login
              </div>
            </div>
            <button
              onClick={() => setShowPinModal(true)}
              className={buttonClass('quiet', 'md')}
            >
              {profile?.hasPin ? "Change PIN" : "Set PIN"}
            </button>
          </div>

          {/* Password */}
          <div className="flex items-center justify-between border-t border-gray-700 pt-4">
            <div>
              <div className="text-white font-medium">Password</div>
              <div className="text-sm text-on-surface-variant">
                <span className="tracking-widest">********</span>
              </div>
            </div>
            <button
              onClick={() => setShowPasswordModal(true)}
              className={buttonClass('quiet', 'md')}
            >
              Change Password
            </button>
          </div>
        </div>
      </Panel>

      {/* Fleet Visibility Section — only for users who can see the Swarm fleet */}
      {canSwarm && (
      <Panel title="Fleet Visibility" className="mb-4">
        <p className="text-sm text-on-surface-variant mb-4">
          Choose which fleets appear in the drone list. Firefly is the day-to-day
          view; turn on Swarm to also see Swarm Drone (D-series) drones.
        </p>

        <div className="space-y-3">
          <Checkbox
            fill
            labelSide="start"
            label="Firefly drones"
            checked={showFirefly}
            onChange={(e) => setShowFirefly(e.target.checked)}
          />

          <Checkbox
            fill
            labelSide="start"
            label="Swarm drones"
            checked={showSwarm}
            onChange={(e) => setShowSwarm(e.target.checked)}
          />

          <Reveal show={!showFirefly && !showSwarm}>
            <p className="text-xs text-yellow-400">
              Both off — the fleet view will be empty.
            </p>
          </Reveal>

          <Reveal show={!!hasFleetChanges}>
            <button
              onClick={saveFleetPrefs}
              disabled={saving}
              className={`${buttonClass('filled', 'md')} w-full mt-2`}
            >
              {saving ? "Saving..." : "Save Fleet Visibility"}
            </button>
          </Reveal>
        </div>
      </Panel>
      )}

      {/* Charge Control Section — VPN-access toggle gates the Open-with-VPN link */}
      {canCharge && (
      <Panel title="Charge Control" className="mb-4">
        <p className="text-sm text-on-surface-variant mb-4">
          When on, charger cards show an “Open with VPN” link to each charger’s own screen.
          Only enable this if your device is connected to the company VPN — the link
          won’t work otherwise.
        </p>
        <Switch
          fill
          labelSide="start"
          label="I have VPN access"
          checked={hasVpnAccess}
          onChange={(e) => toggleVpnAccess(e.target.checked)}
        />
      </Panel>
      )}

      {/* Notifications Section */}
      <Panel title="Notifications" className="mb-4">
        <p className="text-sm text-on-surface-variant mb-4">
          Choose which events send you an email. Off by default — turn on only what you want.
        </p>

        <div className="space-y-3">
          <Checkbox
            fill
            labelSide="start"
            label="Email me when a new event is created"
            description="Sent whenever a show is added — from the New Event form or an accepted Monday import."
            checked={newEventEmail}
            onChange={(e) => setNewEventEmail(e.target.checked)}
          />

          <Reveal show={!!hasNotificationChanges}>
            <button
              onClick={saveNotifications}
              disabled={saving}
              className={`${buttonClass('filled', 'md')} w-full mt-2`}
            >
              {saving ? "Saving..." : "Save Preferences"}
            </button>
          </Reveal>
        </div>
      </Panel>

      {/* Connections Section */}
      <Panel title="Connections" className="mb-4">
        <p className="text-sm text-on-surface-variant mb-4">
          Link your Slack account to send receipts by DM and get nudges in Slack instead of email.
        </p>

        <div className="flex items-start justify-between gap-4">
          <span>
            <span className="block text-white">Slack</span>
            <span className="block text-xs text-on-surface-variant mt-0.5">
              {slackLink?.linked
                ? `Connected. DM any receipt photo or PDF to the ${instance.displayName} app and it lands in your queue.`
                : `Not linked yet. Send the ${instance.displayName} Slack app any message and your account links automatically (it matches your Slack email).`}
            </span>
          </span>
          {slackLink?.linked ? (
            <span className="shrink-0 text-sm font-semibold text-green-400 mt-0.5">Connected</span>
          ) : slackLink?.slack_app_id ? (
            <button
              onClick={() => window.open(`https://slack.com/app_redirect?app=${slackLink.slack_app_id}`, "_blank", "noopener")}
              className={`${buttonClass('filled', 'md')} shrink-0`}
            >
              Open {instance.displayName} in Slack
            </button>
          ) : (
            <span className="shrink-0 text-sm text-on-surface-variant mt-0.5">Not linked</span>
          )}
        </div>
      </Panel>

      <div className="mb-4">
        <ConnectedBots />
      </div>

      {/* Legacy notification toggles - hidden, no delivery backend yet (RMA/maintenance)
      <Panel title="Notifications" className="mb-4">

        <div className="space-y-3">
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-white">RMA status updates</span>
            <input
              type="checkbox"
              checked={rmaUpdates}
              onChange={(e) => setRmaUpdates(e.target.checked)}
              className="rounded text-primary focus:ring-primary"
            />
          </label>

          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-white">Maintenance alerts</span>
            <input
              type="checkbox"
              checked={maintenanceAlerts}
              onChange={(e) => setMaintenanceAlerts(e.target.checked)}
              className="rounded text-primary focus:ring-primary"
            />
          </label>

          {hasNotificationChanges && (
            <button
              onClick={saveNotifications}
              disabled={saving}
              className="w-full py-2 bg-primary hover:bg-green-600 text-black hover:text-white font-semibold rounded transition disabled:opacity-50 mt-2"
            >
              {saving ? "Saving..." : "Save Preferences"}
            </button>
          )}
        </div>
      </Panel>
      */}

      {/* Scanner Settings Section - hidden for now, unused
      <Panel title="Scanner Settings">

        <div>
          <label className="block text-sm text-on-surface-variant mb-2">
            Camera Compatibility Mode: {camCompat}
          </label>
          <select
            value={camCompat}
            onChange={handleCamCompatChange}
            className="w-full px-3 py-2 bg-[#3a3a3a] border border-outline rounded text-white focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="1">Mode 1</option>
            <option value="2">Mode 2</option>
            <option value="3">Mode 3</option>
            <option value="4">Mode 4</option>
          </select>
          <div className="text-xs text-on-surface-variant mt-2 space-y-1">
            <div><b>Mode 1</b> - Universal setting</div>
            <div><b>Mode 2</b> - Bypasses wide angle lenses with slight zoom</div>
            <div><b>Mode 3</b> - Alternate camera library</div>
            <div><b>Mode 4</b> - Alternate camera library (variant)</div>
          </div>
        </div>
      </Panel>
      */}

      {/* Scanner — device-mode setting only. The "Enter Scanner Mode" entry lives
          in the global More nav dropdown so any operator can reach it. */}
      <Panel title="Scanner" className="mb-4" actions={
          <span className="text-[10px] uppercase tracking-widest text-on-surface-variant">
            this device: <span className={deviceMode === 'shared' ? 'text-amber-300/80' : 'text-on-surface-variant'}>{deviceMode}</span>
          </span>
      }>
        <FadeIn key={deviceMode}>
        {deviceMode === 'personal' ? (
          <>
            <p className="text-sm text-on-surface-variant mb-3">
              Marking this browser as a shared shop-floor scanner forces every session to badge+PIN
              before scanning, and stamps the badged operator (not your account) on every maintlog.
            </p>
            <button
              onClick={() => setShowSharedDeviceModal(true)}
              className={`${buttonClass('warn', 'md')} w-full`}
            >
              Set this device as a Shared Scanner
            </button>
          </>
        ) : (
          <p className="text-sm text-on-surface-variant">
            This browser is configured as a shared scanner. To revert, an admin must clear this
            browser's site data.
          </p>
        )}
        </FadeIn>
      </Panel>

      {/* Modals */}
      {showPinModal && (
        <PinModal
          hasPin={profile?.hasPin}
          onClose={() => setShowPinModal(false)}
          onSuccess={() => {
            setShowPinModal(false);
            fetchProfile();
          }}
        />
      )}

      {showPasswordModal && (
        <PasswordModal
          onClose={() => setShowPasswordModal(false)}
          onSuccess={() => {
            setShowPasswordModal(false);
            toast.success("Password changed successfully");
          }}
        />
      )}

      {showSharedDeviceModal && (
        <SetSharedDeviceModal
          onCancel={() => setShowSharedDeviceModal(false)}
          onConfirm={() => {
            setDeviceMode('shared');
            setShowSharedDeviceModal(false);
            toast.success("This device is now marked as a shared scanner");
          }}
        />
      )}
    </div>
</PageLayout>
  );
};

export default SettingsPage;
